import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import type { Vessel, VesselConnectedAccountAccess } from '@ankor-io/common/types/pay'
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownMenu,
} from '@ankor-io/ui-components/src/Dropdown'
import { Loading } from '@ankor-io/ui-components/src/Loading'

import { useListOrganizationVesselsQuery } from '@/api/queries/useVesselQuery'
import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useModuleTheme } from '@/providers/moduleTheme/useModuleTheme'
import { useUserContext } from '@/providers/user/useUserContext'

import NewVesselModal from './NewVesselModal/NewVesselModal'

export const VesselDropdown = () => {
  const { moduleTheme } = useModuleTheme()

  const {
    activeOrganizationId,
    activeVesselId,
    updateActiveVesselId,
    updateActiveConnectedAccountId,
    activeOrganizationRole,
  } = useUserContext()

  const { data, isLoading } = useListOrganizationVesselsQuery({ enabled: Boolean(activeOrganizationId) })

  const showLoader = useMinimumDisplayTime(isLoading, 1000)
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false)

  const vesselList = data?.vessels ?? []
  const selectedVessel: Vessel | null =
    vesselList.find((v) => v.vessel.id === activeVesselId)?.vessel || vesselList[0]?.vessel || null

  if (!activeVesselId && vesselList.length > 0) {
    updateActiveVesselId(vesselList[0]?.vessel.id ?? '')
    updateActiveConnectedAccountId(vesselList[0]?.connectedAccounts[0]?.id ?? '')
  }

  const handleVesselSelect = async (item: VesselConnectedAccountAccess) => {
    const { vessel, connectedAccounts } = item
    if (vessel.id === activeVesselId) return
    window.dispatchEvent(new CustomEvent('vessel-switching'))
    updateActiveVesselId(vessel.id)
    updateActiveConnectedAccountId(connectedAccounts[0]?.id ?? '')
  }

  return (
    <>
      <Dropdown>
        <DropdownButton
          outline
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg border"
        >
          <span className="w-full text-left text-gray-900 dark:text-white truncate">
            {showLoader ? <Loading /> : (selectedVessel?.name ?? 'Select Vessel')}
          </span>
          <ChevronDown className="ml-2 size-4" />
        </DropdownButton>
        <DropdownMenu
          anchor="bottom start"
          className="max-h-96! gap-y-1"
        >
          {vesselList.map((item) => (
            <DropdownItem
              key={item.vessel.id}
              to="/"
              onClick={() => handleVesselSelect(item)}
              className={item.vessel.id === activeVesselId ? 'select-none cursor-none text-white' : undefined}
              style={item.vessel.id === activeVesselId ? { backgroundColor: moduleTheme.dropdownAccentVar } : undefined}
            >
              <span className={clsx(item.vessel.id === activeVesselId && 'font-bold')}>{item.vessel.name}</span>
            </DropdownItem>
          ))}
          {isPrivilegedRole(activeOrganizationRole) && (
            <>
              {vesselList.length > 0 && <DropdownDivider />}
              <DropdownItem
                onClick={() => setIsAccountModalOpen(true)}
                className={`justify-center font-medium ${moduleTheme.text}! hover:text-white!`}
                data-testid="create-new-vessel-option"
              >
                + Create new Vessel
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </Dropdown>

      <NewVesselModal
        setIsOpen={setIsAccountModalOpen}
        isOpen={isAccountModalOpen}
      />
    </>
  )
}
